import React, { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Heart, Home, Users, User } from 'lucide-react';
import { SupportErrorMessage } from '../../../../components/overlays/EngineeringSupportBanner';
import { useAdmissionsApplication } from '../../adapters/liveData';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const asArray = (value: unknown): Array<Record<string, unknown>> =>
  Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];

const readString = (value: unknown): string => String(value ?? '').trim();
const titleCaseWords = (value: string): string =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ');

const formatName = (firstName: unknown, lastName: unknown): string =>
  [readString(firstName), readString(lastName)].filter(Boolean).join(' ').trim();

const buildGrandparentDisplay = (rawValue: unknown): { names: string; location: string; phone: string; family: string } => {
  const record = asRecord(rawValue);
  const names = readString(record.names)
    || [readString(record.grandfatherName), readString(record.grandmotherName)].filter(Boolean).join(' & ')
    || readString(record.name);
  const location = readString(record.location)
    || [readString(record.address), readString(record.city), readString(record.state), readString(record.zip)].filter(Boolean).join(', ');
  const phone = readString(record.phone) || readString(record.phoneNumber);
  const family = readString(record.family) || readString(record.lastName);
  return {
    names: names ? titleCaseWords(names) : '',
    location,
    phone,
    family,
  };
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-[10px] uppercase font-bold text-irongrey/40 mb-1.5 tracking-wider">{label}</p>
    <p className="text-sm font-semibold text-irongrey">
      {value || <span className="italic text-irongrey/30 font-normal">Not specified</span>}
    </p>
  </div>
);

const Card = ({ icon: Icon, title, children }: { icon: React.ElementType; title: string; children: React.ReactNode }) => (
  <section className="bg-white rounded-2xl p-7 shadow-card hover:shadow-soft transition-all border border-gray-100">
    <h3 className="font-bold text-lg text-irongrey mb-7 flex items-center gap-3">
      <span className="w-[36px] h-[36px] flex items-center justify-center bg-primary/10 rounded-lg text-primary shrink-0">
        <Icon size={20} />
      </span>
      {title}
    </h3>
    {children}
  </section>
);

export const FamilyTab: React.FC = () => {
  const { applicationId } = useOutletContext<{ applicationId: string }>();
  const query = useAdmissionsApplication(applicationId);
  const application = query.data as unknown as Record<string, unknown> | undefined;
  const applicant = asRecord(application?.applicant);
  const primaryContact = asRecord(applicant.primaryContact);
  const address = asRecord(primaryContact.address);
  const parents = asArray(applicant.parents);
  const father = parents.find((p) => readString(p.relation).toLowerCase() === 'father') ?? null;
  const mother = parents.find((p) => readString(p.relation).toLowerCase() === 'mother') ?? null;

  const otherSiblings = asArray(applicant.otherSiblings);
  const paternalGrandparents = buildGrandparentDisplay(applicant.paternalGrandparents);
  const maternalGrandparents = buildGrandparentDisplay(applicant.maternalGrandparents);

  const homeAddress = useMemo(() => {
    const street = readString(address.street);
    const city = readString(address.city);
    const state = readString(address.state);
    const zip = readString(address.zipCode);
    const line1 = [street].filter(Boolean).join('');
    const line2 = [city, state, zip].filter(Boolean).join(', ');
    return [line1, line2].filter(Boolean).join('\n');
  }, [address.city, address.state, address.street, address.zipCode]);

  return (
    <section data-testid="admissions-detail-family-tab" className="flex flex-col gap-4 pb-8">
      {query.isLoading ? <p className="text-sm text-irongrey/50">Loading…</p> : null}
      {query.error ? (
        <SupportErrorMessage>Unable to load family details right now.</SupportErrorMessage>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card icon={Home} title="Home Contact">
          <div className="space-y-6">
            <Field label="Family Name" value={readString(applicant.familyName)} />
            <Field label="Primary Contact" value={formatName(primaryContact.firstName, primaryContact.lastName)} />
            <Field label="Email" value={readString(primaryContact.email)} />
            <Field label="Phone" value={readString(primaryContact.phone)} />
            <Field label="Home Address" value={homeAddress} />
            <Field label="Home Phone" value={readString(applicant.homePhone)} />
          </div>
        </Card>

        <Card icon={Users} title="Status & Affiliation">
          <div className="space-y-5">
            <Field label="Marital Status" value={readString(applicant.maritalStatus)} />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Adopted Child?" value={readString(applicant.adoptedChild)} />
              <Field label="Step-parent?" value={readString(applicant.stepParent)} />
            </div>
            <Field label="Current Shul" value={readString(applicant.synagogue)} />
            <Field label="RAV" value={readString(applicant.rav)} />
          </div>
        </Card>

        <Card icon={Heart} title="Other Siblings">
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-100 pb-2">
              <div className="col-span-5 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Name</div>
              <div className="col-span-2 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Age</div>
              <div className="col-span-5 text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">School</div>
            </div>
            {otherSiblings.map((sib, i) => (
              <div key={readString(sib.id) || `sib-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-5 text-sm font-semibold text-irongrey truncate">{readString(sib.name)}</div>
                <div className="col-span-2 text-sm font-medium text-irongrey/60">{readString(sib.age)}</div>
                <div className="col-span-5 text-sm font-medium text-irongrey/60 truncate">{readString(sib.school)}</div>
              </div>
            ))}
            {otherSiblings.length === 0 && (
              <p className="text-sm text-irongrey/40 italic">No other siblings listed</p>
            )}
          </div>
        </Card>
      </div>

      <section className="bg-white rounded-2xl p-7 shadow-card hover:shadow-soft transition-all border border-gray-100">
        <h3 className="font-bold text-lg text-irongrey mb-7 flex items-center gap-3">
          <span className="w-[36px] h-[36px] flex items-center justify-center bg-primary/10 rounded-lg text-primary shrink-0">
            <User size={20} />
          </span>
          Parents Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100">
            <h4 className="font-bold text-xs text-irongrey/60 mb-5 uppercase tracking-widest border-b border-gray-200 pb-3 flex items-center gap-2">
              <User size={14} /> Father
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Title" value={readString(father?.title)} />
              <Field label="Full Name" value={formatName(father?.firstName, father?.lastName)} />
              <Field label="Cell Phone" value={readString(father?.phone)} />
              <Field label="Email" value={readString(father?.email)} />
              <Field label="Occupation" value={readString(father?.occupation)} />
              <Field label="Name of Firm" value={readString(father?.firm)} />
            </div>
          </div>

          <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100">
            <h4 className="font-bold text-xs text-irongrey/60 mb-5 uppercase tracking-widest border-b border-gray-200 pb-3 flex items-center gap-2">
              <User size={14} /> Mother
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Title" value={readString(mother?.title)} />
              <Field label="Full Name" value={formatName(mother?.firstName, mother?.lastName)} />
              <Field label="Maiden Name" value={readString(mother?.maidenName)} />
              <Field label="Cell Phone" value={readString(mother?.phone)} />
              <Field label="Email" value={readString(mother?.email)} />
              <Field label="Occupation" value={readString(mother?.occupation)} />
              <Field label="Name of Firm" value={readString(mother?.firm)} />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-2xl p-7 shadow-card hover:shadow-soft transition-all border border-gray-100">
        <h3 className="font-bold text-lg text-irongrey mb-7 flex items-center gap-3">
          <span className="w-[36px] h-[36px] flex items-center justify-center bg-primary/10 rounded-lg text-primary shrink-0">
            <Users size={20} />
          </span>
          Grandparents Information
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card icon={Users} title="Paternal Grandparents">
            <div className="space-y-4">
              <Field label="Names" value={paternalGrandparents.names} />
              <Field label="Location" value={paternalGrandparents.location} />
              <Field label="Phone" value={paternalGrandparents.phone} />
              <Field label="Family" value={paternalGrandparents.family} />
            </div>
          </Card>
          <Card icon={Users} title="Maternal Grandparents">
            <div className="space-y-4">
              <Field label="Names" value={maternalGrandparents.names} />
              <Field label="Location" value={maternalGrandparents.location} />
              <Field label="Phone" value={maternalGrandparents.phone} />
              <Field label="Family" value={maternalGrandparents.family} />
            </div>
          </Card>
        </div>
      </section>
    </section>
  );
};
